import React, { useState } from "react";
import { Token, Typeahead } from "react-bootstrap-typeahead";
import StateHelper from "../../utils/StateHelper";

import "./Typeahead.css";
import { ColumnIds, PageName, AUTOCOMPLETE_ITEM_PADDING_STYLE_CLICKABLE } from "../../utils/types";
import { InputGroup } from "react-bootstrap";
import AutocompleteIncludeExclude from "./AutocompleteIncludeExclude";

//https://github.com/ericgio/react-bootstrap-typeahead

function isReadOnly(option) {
  const pageState = StateHelper.getPageState();
  if (pageState.page === PageName.performanceReports) {
    if (option.id === ColumnIds.campaignSource) {
      return true;
    }
  }

  if (pageState.pivotBy) {
    if (option.id === ColumnIds.date || option.id === ColumnIds.week || option.id === ColumnIds.month) {
      return true;
    }
  }
  return false;
}

export default function Autocomplete(props) {
  const [showAllTokens, setShowAllTokens] = useState(false);

  // Handle click for keepDropdownOpen mode
  const handleKeepOpenClick = (option) => {
    if (!props.multiple) {
      // For single select, just select the option
      props.onChange([option]);
      return;
    }

    // For multiple select, toggle the option
    const currentSelected = props.selected || [];
    const isSelected = currentSelected.some(selected => {
      // Handle different comparison scenarios
      if (typeof selected === 'object' && selected !== null && typeof option === 'object' && option !== null) {
        // Try to find a common ID field
        if (selected.id !== undefined && option.id !== undefined) {
          return selected.id === option.id;
        }
        if (selected.code !== undefined && option.code !== undefined) {
          return selected.code === option.code;
        }
        // Fallback to JSON comparison
        return JSON.stringify(selected) === JSON.stringify(option);
      }
      return selected === option;
    });

    let newSelection;
    if (isSelected) {
      // Remove from selection
      newSelection = currentSelected.filter(selected => {
        if (typeof selected === 'object' && selected !== null && typeof option === 'object' && option !== null) {
          if (selected.id !== undefined && option.id !== undefined) {
            return selected.id !== option.id;
          }
          if (selected.code !== undefined && option.code !== undefined) {
            return selected.code !== option.code;
          }
          return JSON.stringify(selected) !== JSON.stringify(option);
        }
        return selected !== option;
      });
    } else {
      // Add to selection
      newSelection = [...currentSelected, option];
    }

    props.onChange(newSelection);
  };

  // Helper to create a padded, clickable wrapper for keepDropdownOpen mode
  const createPaddedWrapper = (content, option) => (
    <div
      style={AUTOCOMPLETE_ITEM_PADDING_STYLE_CLICKABLE}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        handleKeepOpenClick(option);
      }}
    >
      {content}
    </div>
  );

  // Wrap renderMenuItemChildren to add stopPropagation when keepDropdownOpen is true
  const wrappedRenderMenuItemChildren = props.keepDropdownOpen && props.renderMenuItemChildren
    ? (option, menuProps, index) => {
        const originalContent = props.renderMenuItemChildren(option, menuProps, index);
        return createPaddedWrapper(originalContent, option);
      }
    : props.renderMenuItemChildren;

  // Default renderMenuItemChildren when keepDropdownOpen is true but no custom one provided
  const defaultRenderMenuItemChildren = props.keepDropdownOpen && !props.renderMenuItemChildren
    ? (option, menuProps, index) => {
        const label = typeof props.labelKey === 'function'
          ? props.labelKey(option)
          : option[props.labelKey] || option;
        return createPaddedWrapper(label, option);
      }
    : undefined;

  let renderToken = null;
  if (props.renderToken) {
    renderToken = (option, props, idx) => {
      return (
        <Token
          key={idx}
          option={option}
          onRemove={isReadOnly(option) ? undefined : () => props.onRemove(option)}
        >
          {option.name}
        </Token>
      );
    };
  } else if (props.maxDisplayTokens && props.maxDisplayTokens > 0) {
    // Custom renderToken to limit displayed tokens
    renderToken = (option, tokenProps, idx) => {
      const maxDisplay = props.maxDisplayTokens;
      // Access selected from props since tokenProps might not have it
      const totalSelected = (props.selected || []).length;

      // Helper to get label for an option
      const getLabel = (opt) => {
        if (opt.name) return opt.name;
        if (typeof props.labelKey === 'function') return props.labelKey(opt);
        if (props.labelKey && opt[props.labelKey]) return opt[props.labelKey];
        return opt.label || opt.toString();
      };

      // When expanded, show all tokens and put the hide button at the end
      if (showAllTokens) {
        // Show all tokens normally when expanded
        if (idx < totalSelected - 1) {
          return (
            <Token
              key={idx}
              option={option}
              onRemove={isReadOnly(option) ? undefined : () => tokenProps.onRemove(option)}
            >
              {getLabel(option)}
            </Token>
          );
        } else if (idx === totalSelected - 1) {
          // Show the last token and the hide button at the very end
          return (
            <>
              <Token
                key={idx}
                option={option}
                onRemove={isReadOnly(option) ? undefined : () => tokenProps.onRemove(option)}
              >
                {getLabel(option)}
              </Token>
              <Token
                key={`hide-${idx}`}
                option={{}} // dummy option for the hide button
                onRemove={undefined}
              >
                <span 
                  style={{ cursor: 'pointer', userSelect: 'none' }} 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllTokens(false);
                  }}
                  title="Click to hide extra items"
                >
                  « hide ({totalSelected - maxDisplay})
                </span>
              </Token>
            </>
          );
        }
      } else {
        // When collapsed, show only up to maxDisplay items
        if (idx < maxDisplay) {
          return (
            <Token
              key={idx}
              option={option}
              onRemove={isReadOnly(option) ? undefined : () => tokenProps.onRemove(option)}
            >
              {getLabel(option)}
            </Token>
          );
        } else if (idx === maxDisplay && totalSelected > maxDisplay) {
          // Show "+X more" token for remaining items (when collapsed)
          const remainingCount = totalSelected - maxDisplay;
          return (
            <Token
              key={`more-${idx}`}
              option={option}
              onRemove={undefined}
            >
              <span 
                style={{ cursor: 'pointer', userSelect: 'none' }} 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllTokens(true);
                }}
                title="Click to show all selected items"
              >
                +{remainingCount} more
              </span>
            </Token>
          );
        }
      }
      
      // Don't render when collapsed and beyond maxDisplay
      return null;
    };
  }

  const finalRenderMenuItemChildren = wrappedRenderMenuItemChildren || defaultRenderMenuItemChildren || props.renderMenuItemChildren;

  return (
    <InputGroup className={props.className}>
      {
        props.useIncludeExclude ?
          <AutocompleteIncludeExclude
            defaultIncludeExclude={props.defaultIncludeExclude}
            includeExcludeOnChange={props.includeExcludeOnChange}
            disabled={props.disabled}
          />
        :
          null
      }
      <Typeahead
        allowNew={props.allowNew||undefined}
        renderMenu={props.renderMenu||undefined}
        renderMenuItem={props.renderMenuItem||undefined}
        renderMenuItemChildren={finalRenderMenuItemChildren||undefined}
        size="sm"
        newSelectionPrefix={props.newSelectionPrefix}
        onInputChange={props.onInputChange||undefined}
        placeholder={props.placeholder}
        id={"id" + props.reactKey}
        key={props.reactKey}
        labelKey={props.labelKey}
        multiple={props.multiple}
        onChange={props.onChange}
        onKeyDown={props.onKeyDown}
        options={props.options}
        onBlur={props.onBlur}
        style={{...props.style, fontSize: 11}}
        onMenuToggle={props.onMenuToggle}
        selected={props.selected}
        clearButton
        filterBy={props.filterBy||undefined}
        paginate={props.paginate||undefined}
        maxResults={props.maxResults||undefined}
        ref={props.reactRef}
        disabled={props.disabled}
        renderToken={renderToken||undefined}
      />
    </InputGroup>
  );
}